import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import itemsApi from '../itemsApi';
import { loadSalt } from '../authentication/authenticationReducer';
import { addSuccessNotification, addErrorNotification } from '../common/notification/notificationReducer';
import transformer from '../common/helpers/transformer';
import type { DecryptedItem } from '../common/types/items';
import type { AppDispatch, Store, TransfersState } from '../store';
import { Transfer } from '../common/types/transfer';

const initialState: TransfersState = {
  transfers: [],
  currentTransfer: null,
  status: null,
  errorMessage: null,
};

const loadTransfers = createAsyncThunk<
  DecryptedItem<Transfer>[],
  undefined,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('transfers/loadTransfers', async (_, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  await dispatch(loadSalt());
  const data = await itemsApi.loadItems('transfer');
  if (data != null) {
    return await Promise.all(
      data.items.map(async (item): Promise<DecryptedItem<Transfer>> => {
        try {
          const transfer = await transformer.fromBase64ToObjectWithDecryption<Transfer>(item.payload);
          return {
            ...item,
            payload: transfer,
          };
        } catch (error) {
          console.log('Fehler: ', error);
          dispatch(addErrorNotification('Die Übergabe konnte nicht entschlüsselt werden.'));
          return {
            ...item,
            payload: { state: false },
          };
        }
      }),
    );
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Übergaben konnten nicht geladen werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return [];
});

const loadTransferById = createAsyncThunk<
  DecryptedItem<Transfer> | null,
  string,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('transfers/loadTransferById', async (id: string, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const data = await itemsApi.loadItemById(id);
  if (data != null && data.item != null) {
    const transfer = await transformer.fromBase64ToObjectWithDecryption<Transfer>(data.item.payload);
    return {
      ...data.item,
      payload: transfer,
    };
  }
  if (state.authentication.isInitialized) {
    const errorMessage = `Die Übergabe mit der ID ${id} konnte nicht geladen werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return null;
});

const addTransfer = createAsyncThunk<
  string | null,
  { id: string; payload: string },
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('transfers/addTransfer', async ({ id, payload }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const item = await itemsApi.addItem(id, payload, 'transfer');
  if (item != null) {
    dispatch(addSuccessNotification('Die Übergabe wurde hinzugefügt.'));
    return item.id;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = `Die Übergabe mit der ID ${id} konnte nicht hinzugefügt werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return null;
});

const editTransfer = createAsyncThunk<
  boolean,
  { id: string; payload: string },
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('transfers/editTransfer', async ({ id, payload }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const isEdited = await itemsApi.editItem(id, payload);
  if (isEdited) {
    dispatch(addSuccessNotification('Die Änderungen wurden gespeichert.'));
    return isEdited;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Änderungen konnten nicht gespeichert werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const deleteTransfer = createAsyncThunk<
  boolean,
  string,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('transfers/deleteTransfer', async (id, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const isDeleted = await itemsApi.deleteItem(id);
  if (isDeleted) {
    dispatch(addSuccessNotification('Die Übergabe wurde gelöscht.'));
    return isDeleted;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = `Die Übergabe mit der ID ${id} konnte nicht gelöscht werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const transfersSlice = createSlice({
  name: 'transfers',
  initialState,
  reducers: {
    clearCurrentTransfer(state) {
      state.currentTransfer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTransfers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadTransfers.fulfilled, (state, action) => {
        state.status = 'idle';
        state.transfers = action.payload;
      })
      .addCase(loadTransfers.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(loadTransferById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadTransferById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.currentTransfer = action.payload;
      })
      .addCase(loadTransferById.rejected, (state, action) => {
        state.status = 'idle';
        state.currentTransfer = null;
        state.errorMessage = action.payload;
      })
      .addCase(addTransfer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addTransfer.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(addTransfer.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(editTransfer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(editTransfer.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(editTransfer.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(deleteTransfer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteTransfer.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(deleteTransfer.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase('resetState', (state) => {
        state = initialState;
      });
  },
});

const { clearCurrentTransfer } = transfersSlice.actions;

export { loadTransfers, loadTransferById, clearCurrentTransfer, addTransfer, editTransfer, deleteTransfer };

export default transfersSlice.reducer;
